/* eslint-disable import/no-anonymous-default-export */
import { ICreatePromotionRequest, IPromotion, ISendPromotionRequest, IUpdatePromotionRequest } from '../domain/interfaces/Promotions';
import HttpService from './http.service';



class PromotionService {
  async getActivePromotions(): Promise<IPromotion[]> {
    try {
      const response = await HttpService.get<IPromotion[]>('/api/promotions/active');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getCompanyPromotions(companyId: string): Promise<IPromotion[]> {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');

      const response = await HttpService.get<IPromotion[]>(`/api/promotions/company/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createPromotion(promotionData: ICreatePromotionRequest): Promise<IPromotion> {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');

      const response = await HttpService.post<IPromotion>('/api/promotions', promotionData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error: any) {

      // Manejar errores específicos del API
      if (error?.response) {
        const { status, data } = error.response;
        switch (status) {
          case 400:
            throw new Error(data?.message || 'Datos inválidos');
          case 401:
            throw new Error('No estás autenticado. Por favor inicia sesión.');
          case 403:
            throw new Error('No tienes permisos para crear promociones para esta empresa.');
          case 404:
            throw new Error('Empresa no encontrada.');
          case 500:
            throw new Error('Error interno del servidor. Intenta nuevamente.');
          default:
            throw new Error(data?.message || 'Error al crear la promoción');
        }
      }

      throw new Error('Error de conexión. Verifica tu conexión a internet.');
    }
  }

  async updatePromotion(promotionId: string, updateData: IUpdatePromotionRequest): Promise<IPromotion> {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');

      const response = await HttpService.put<IPromotion>(`/api/promotions/${promotionId}`, updateData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deletePromotion(promotionId: string): Promise<void> {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');

      await HttpService.delete(`/api/promotions/${promotionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      throw error;
    }
  }

  async activatePromotion(promotionId: string): Promise<IPromotion> {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');

      const response = await HttpService.put<IPromotion>(`/api/promotions/${promotionId}/activate`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error: any) {

      // Manejar errores específicos del API
      if (error?.response) {
        const { status, data } = error.response;
        switch (status) {
          case 400:
            throw new Error(data?.message || 'No se puede activar la promoción');
          case 401:
            throw new Error('No estás autenticado. Por favor inicia sesión.');
          case 403:
            throw new Error('No tienes permisos para activar esta promoción.');
          case 404:
            throw new Error('Promoción no encontrada.');
          default:
            throw new Error(data?.message || 'Error al activar la promoción');
        }
      }

      throw new Error('Error de conexión. Verifica tu conexión a internet.');
    }
  }

  /**
   * Desactiva una promoción
   * @param promotionId ID de la promoción
   * @returns Promoción actualizada
   */
  async deactivatePromotion(promotionId: string): Promise<IPromotion> {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');

      const response = await HttpService.put<IPromotion>(`/api/promotions/${promotionId}/deactivate`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error: any) {

      // Manejar errores específicos del API
      if (error?.response) {
        const { status, data } = error.response;
        switch (status) {
          case 400:
            throw new Error(data?.message || 'No se puede desactivar la promoción');
          case 401:
            throw new Error('No estás autenticado. Por favor inicia sesión.');
          case 403:
            throw new Error('No tienes permisos para desactivar esta promoción.');
          case 404:
            throw new Error('Promoción no encontrada.');
          default:
            throw new Error(data?.message || 'Error al desactivar la promoción');
        }
      }

      throw new Error('Error de conexión. Verifica tu conexión a internet.');
    }
  }

  /**
   * @deprecated Usar activatePromotion o deactivatePromotion en su lugar
   * Activa/desactiva una promoción
   * @param promotionId ID de la promoción
   * @param isActive Estado activo
   * @returns Promoción actualizada
   */
  async togglePromotionStatus(promotionId: string, isActive: boolean): Promise<IPromotion> {
    // Redirigir a los nuevos métodos
    return isActive ? this.activatePromotion(promotionId) : this.deactivatePromotion(promotionId);
  }

  /**
   * Sube una imagen para la promoción
   * @param file Archivo de imagen
   * @returns URL de la imagen subida
   */
  async uploadPromotionImage(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await HttpService.post<{ imageUrl: string }>('/api/promotions/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data.imageUrl;
    } catch (error) {
      throw error;
    }
  }

  async requestPromotion(data: ISendPromotionRequest): Promise<string> {
    try {
      const response = await HttpService.post<string>(`/api/Promotions/request-promotion`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

}

//export default new PromotionService();
const promotionService = new PromotionService();
export default promotionService;
